import React, { useEffect, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from "../config/apiconfig";

export const DocumentTemplates = () => {
  const { user } = useAuth();
  const categories = [
    { key: 'job_post', label: 'Job Post Template' },
    { key: 'call_for_interview', label: 'Call for Interview' },
    { key: 'offer_letter', label: 'Offer Letter Template' },
    { key: 'internship_offer', label: 'Internship/Job Offer Letter' },
    { key: 'rejection', label: 'Application Rejection' },
  ];

  const [activeCategory, setActiveCategory] = useState(categories[0].key);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', category: categories[0].key, body_html: '' });

  const fetchTemplates = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/templates/documents?created_by=${user.id}&category=${activeCategory}`);
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError('Failed to load document templates.');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeCategory]);

  const onEdit = (tpl) => {
    setEditingId(tpl.id);
    setFormData({ title: tpl.title, category: tpl.category, body_html: tpl.body_html });
    setShowForm(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/templates/documents/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setTemplates(templates.filter(t => t.id !== id));
    } catch (e) {
      setError('Failed to delete template');
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingId ? `${API_BASE_URL}/api/templates/documents/${editingId}` : `${API_BASE_URL}/api/templates/documents`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, created_by: user.id })
      });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();
      if (editingId) setTemplates(templates.map(t => (t.id === editingId ? saved : t)));
      else setTemplates([saved, ...templates]);
      setShowForm(false);
      setEditingId(null);
      setFormData({ title: '', category: activeCategory, body_html: '' });
    } catch (e) {
      setError('Failed to save template');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Document Templates</h2>
        <button
          onClick={() => { setShowForm(!showForm); setEditingId(null); setFormData({ title: '', category: activeCategory, body_html: '' }); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="w-4 h-4 mr-2" /> {showForm ? 'Cancel' : 'Create New'}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {categories.map(c => (
          <button
            key={c.key}
            onClick={() => { setActiveCategory(c.key); setFormData(fd => ({ ...fd, category: c.key })); }}
            className={`px-3 py-1.5 rounded border ${activeCategory === c.key ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-300 text-gray-700'}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-3 text-red-700 text-sm">{error}</div>
      )}

      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <form onSubmit={onSubmit}>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body (HTML allowed)</label>
                <textarea
                  value={formData.body_html}
                  onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={10}
                  placeholder="Use placeholders like {{candidate_name}}, {{job_title}}, {{company_name}}"
                  required
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
              <button type="submit" className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700">Save Template</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-600">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-10 text-gray-500">No templates in this category.</div>
      ) : (
        <div className="bg-white rounded-lg border">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {templates.map(t => (
                <tr key={t.id}>
                  <td className="px-6 py-4 text-sm text-gray-900">{t.title}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{t.category}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{new Date(t.updated_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 space-x-3">
                    <button onClick={() => onEdit(t)} className="text-blue-600 hover:text-blue-800">Edit</button>
                    <button onClick={() => onDelete(t.id)} className="text-red-600 hover:text-red-800">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
